import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TreatmentPlanAppointment } from "../../../../services/treatmentPlanService";
import ExplanationTooltip from "../../../../components/ExplanationTooltip/ExplanationTooltip";
import {
  deduplicateCdtCodesStructured,
  deduplicateCdtDescriptions,
} from "../../../../utils/cdtCodeUtils";
import {
  AppointmentContainer,
  AppointmentHeader,
  AppointmentTitle,
  AppointmentBadge,
  AppointmentMeta,
  MetaItem,
  MetaLabel,
  MetaValue,
  ProcedureCard,
  ProcedureLayout,
  ProcedureContentColumn,
  ProcedureDetailsColumn,
  ProcedureTitle,
  ProcedureCdtExplanation,
  ProcedureDetailItemCdt,
  ProcedureDetailItemFee,
  ProcedureDetailLabel,
  ProcedureDetailValue,
  ProcedureDetailHint,
} from "./TreatmentAppointment.styled";

interface TreatmentAppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
  showTooltip?: boolean;
}

const TreatmentAppointment: React.FC<TreatmentAppointmentProps> = ({
  appointment,
  appointmentNumber,
  showTooltip = true,
}) => {
  const { t } = useTranslation();

  // Get total fee for the appointment - use totalFee from API if available
  const getAppointmentFee = () => {
    if (appointment.totalFee != null) {
      return appointment.totalFee;
    }
    // Fallback: calculate from steps
    let total = 0;
    appointment.steps.forEach((step) => {
      if (step.fee != null) {
        total += step.fee;
      } else {
        step.codeSuggestions.forEach((code) => {
          total += code.defaultFee || 0;
        });
      }
    });
    return total;
  };

  // Get total duration for the appointment - use totalDurationMinutes from API if available
  const getAppointmentDuration = () => {
    if (appointment.totalDurationMinutes != null) {
      return `${appointment.totalDurationMinutes} min`;
    }
    // Fallback: calculate from steps
    let totalMinutes = 0;
    appointment.steps.forEach((step) => {
      totalMinutes += step.durationMinutes ?? 20;
    });
    return `${totalMinutes} min`;
  };

  // Get procedure fee - use step.fee if available, otherwise calculate from code suggestions
  const getProcedureFee = (step: TreatmentPlanAppointment["steps"][0]) => {
    if (step.fee != null) {
      return step.fee;
    }
    return step.codeSuggestions.reduce(
      (sum, code) => sum + (code.defaultFee || 0),
      0,
    );
  };

  // Get procedure explanation from CDT code descriptions (deduplicated with multiplier)
  const getProcedureExplanation = (
    step: TreatmentPlanAppointment["steps"][0],
  ) => {
    return deduplicateCdtDescriptions(step.codeSuggestions).join("\n");
  };

  const renderCode = (code: string, count: number, key: string) => (
    <Box key={key} component="span" sx={{ whiteSpace: "nowrap" }}>
      {code}
      {count > 1 && (
        <Box component="span" sx={{ color: "#6B7A94" }}>
          {" "}
          x{count}
        </Box>
      )}
    </Box>
  );

  const formatCdtCodes = (
    entries: ReturnType<typeof deduplicateCdtCodesStructured>,
  ): React.ReactNode => {
    if (entries.length === 0) return "-";

    const hasMultipliers = entries.some((e) => e.count > 1);
    const perRow = hasMultipliers && entries.length <= 2 ? 1 : 2;

    const rows: (typeof entries)[] = [];
    for (let i = 0; i < entries.length; i += perRow) {
      rows.push(entries.slice(i, i + perRow));
    }

    return (
      <>
        {rows.map((row, rowIdx) => (
          <React.Fragment key={rowIdx}>
            {rowIdx > 0 && "\n"}
            {row.map((entry, colIdx) => (
              <React.Fragment key={entry.code}>
                {colIdx > 0 && ", "}
                {renderCode(entry.code, entry.count, entry.code)}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Get option label from conditionalPath (e.g., "A" -> "Option A", "Option 1" -> "1")
  const getOptionLabel = () => {
    if (
      !appointment.conditionalPath ||
      appointment.conditionalPath.trim() === ""
    ) {
      return null;
    }
    const rawPath = appointment.conditionalPath.trim();
    // If conditionalPath already starts with "Option", extract just the label part
    // Otherwise, return as-is (translation will add "Option" prefix)
    return rawPath.toLowerCase().startsWith("option")
      ? rawPath.replace(/^option\s*/i, "")
      : rawPath;
  };

  const optionLabel = getOptionLabel();
  const totalFee = getAppointmentFee();
  const duration = getAppointmentDuration();

  return (
    <AppointmentContainer>
      <AppointmentHeader>
        <AppointmentTitle>
          {t("treatmentPlan.appointment", { number: appointmentNumber })}
          {optionLabel && (
            <AppointmentBadge>
              {t("treatmentPlan.optionLabel", { label: optionLabel })}
            </AppointmentBadge>
          )}
        </AppointmentTitle>
        <AppointmentMeta>
          <MetaItem>
            <MetaLabel>{t("treatmentPlan.fee")}</MetaLabel>
            <MetaValue>${totalFee.toLocaleString("en-US")}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t("treatmentPlan.duration")}</MetaLabel>
            <MetaValue>{duration}</MetaValue>
          </MetaItem>
        </AppointmentMeta>
      </AppointmentHeader>

      {appointment.steps
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((step) => {
          const explanation = getProcedureExplanation(step);
          const fee = getProcedureFee(step);
          const cdtEntries = deduplicateCdtCodesStructured(
            step.codeSuggestions,
          );
          const hasMultipliersInStep = cdtEntries.some((e) => e.count > 1);
          const cdtColumnWidth =
            hasMultipliersInStep && cdtEntries.length >= 3 ? 160 : 120;
          return (
            <ProcedureCard key={step.id}>
              <ProcedureLayout>
                <ProcedureContentColumn>
                  <ProcedureTitle>{step.name}</ProcedureTitle>
                  {step.description && (
                    <ProcedureCdtExplanation>
                      {step.description}
                    </ProcedureCdtExplanation>
                  )}
                </ProcedureContentColumn>
                <ProcedureDetailsColumn>
                  <ProcedureDetailItemCdt sx={{ width: `${cdtColumnWidth}px` }}>
                    <ProcedureDetailLabel>
                      {t("treatmentPlan.cdtCode")}
                    </ProcedureDetailLabel>
                    <ProcedureDetailValue
                      sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
                    >
                      {formatCdtCodes(cdtEntries)}
                    </ProcedureDetailValue>
                  </ProcedureDetailItemCdt>
                  <ProcedureDetailItemFee>
                    <ProcedureDetailLabel>
                      {t("treatmentPlan.fee")}
                    </ProcedureDetailLabel>
                    <ProcedureDetailValue sx={{ wordBreak: "break-word" }}>
                      ${fee.toLocaleString("en-US")}{" "}
                      <ProcedureDetailHint>
                        {t("treatmentPlan.basedOnClinicRates")}
                      </ProcedureDetailHint>
                    </ProcedureDetailValue>
                  </ProcedureDetailItemFee>
                </ProcedureDetailsColumn>
              </ProcedureLayout>
              {explanation && showTooltip && <ExplanationTooltip explanation={explanation} />}
            </ProcedureCard>
          );
        })}
    </AppointmentContainer>
  );
};

export default TreatmentAppointment;
